import type { TariffRateAnalysisRow, TariffStatus } from '../../../apis/types'
import {
    compareRateRows,
    validationTableValue,
    type SortState,
    type ValidationTableSortKey,
} from './tariffRateSort'

export type DeltaTone = 'neutral' | 'up-light' | 'up-medium' | 'up-strong' | 'down-light' | 'down-medium' | 'down-strong'

/** Color by the displayed absolute delta so the same number gets the same tone. */
export function deltaTone(delta: number | null): DeltaTone {
    if (delta == null || Math.abs(delta) < 0.001) {
        return 'neutral'
    }
    const magnitude =
        Math.abs(delta) >= 0.03 ? 'strong' : Math.abs(delta) >= 0.01 ? 'medium' : 'light'
    return `${delta > 0 ? 'up' : 'down'}-${magnitude}`
}

export const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
    neutral: 'text-slate-600 dark:text-slate-300',
    'up-light': 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200',
    'up-medium': 'bg-sky-100 text-sky-900 dark:bg-sky-900/60 dark:text-sky-100',
    'up-strong': 'bg-sky-200 text-sky-950 dark:bg-sky-800 dark:text-sky-50',
    'down-light': 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200',
    'down-medium': 'bg-orange-100 text-orange-900 dark:bg-orange-900/60 dark:text-orange-100',
    'down-strong': 'bg-orange-200 text-orange-950 dark:bg-orange-800 dark:text-orange-50',
}

export function formatDelta(delta: number | null): string {
    if (delta == null) {
        return '—'
    }
    const arrow = delta > 0 ? '↑ +' : delta === 0 ? '• ' : '↓ '
    return `${arrow}${delta.toFixed(6)}`
}

export function formatRate(value: number): string {
    return value.toFixed(6)
}

const RATESET_DAY = /^(\d{4})-(\d{2})-(\d{2})/

/** UTC calendar date from an analysis `rateEffectiveDate` (`YYYY-MM-DD` or ISO). */
export function parseRatesetEffectiveDate(value: string): Date | null {
    const match = RATESET_DAY.exec(value.slice(0, 10))
    if (match == null) return null
    return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

/** Twelve calendar months before `now`, at UTC midnight. */
export function ratesetAgeCutoff(now: Date): Date {
    const cutoff = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    cutoff.setUTCMonth(cutoff.getUTCMonth() - 12)
    return cutoff
}

/**
 * Recent enough to keep in the default rate-validation view.
 * Always keep the newest rateset for a schedule; hide older ones past the 12-month cutoff.
 */
export function isOlderHiddenRateset(
    effectiveDate: string,
    newestEffectiveDate: string,
    now: Date
): boolean {
    if (effectiveDate === newestEffectiveDate) return false
    const parsed = parseRatesetEffectiveDate(effectiveDate)
    if (parsed == null) return false
    return parsed.getTime() < ratesetAgeCutoff(now).getTime()
}

const TARIFF_STATUSES: ReadonlySet<string> = new Set([
    'PENDING',
    'ACTIVE',
    'INACTIVE',
    'WAITING_FOR_APPROVAL',
    'APPROVED',
    'ARCHIVED',
])

export function asTariffStatus(value: string | null | undefined): TariffStatus | null {
    if (value == null || !TARIFF_STATUSES.has(value)) return null
    return value as TariffStatus
}

export type ValidationRatesetGroup = {
    key: string
    schedule: string
    rateEffectiveDate: string
    ratesetStatus: TariffStatus | null
    rows: TariffRateAnalysisRow[]
}

export type ValidationScheduleGroup = {
    schedule: string
    tariffName: string | null
    tariffStatus: TariffStatus | null
    ratesets: ValidationRatesetGroup[]
}

/** Nest analysis rows by schedule, then rateset date (newest first). */
export function buildValidationScheduleGroups(
    rows: TariffRateAnalysisRow[],
    sort: SortState<ValidationTableSortKey> | null
): ValidationScheduleGroup[] {
    const scheduleOrder: string[] = []
    const bySchedule = new Map<string, Map<string, TariffRateAnalysisRow[]>>()
    const meta = new Map<string, { name: string | null; status: TariffStatus | null }>()
    const ratesetStatus = new Map<string, TariffStatus | null>()

    for (const row of rows) {
        if (!bySchedule.has(row.schedule)) {
            scheduleOrder.push(row.schedule)
            bySchedule.set(row.schedule, new Map())
            meta.set(row.schedule, {
                name: row.tariffName ?? null,
                status: asTariffStatus(row.tariffStatus),
            })
        }
        const ratesets = bySchedule.get(row.schedule)
        if (ratesets == null) continue
        const date = row.rateEffectiveDate
        const list = ratesets.get(date) ?? []
        list.push(row)
        ratesets.set(date, list)
        const key = `${row.schedule}|${date}`
        if (!ratesetStatus.has(key)) {
            ratesetStatus.set(key, asTariffStatus(row.ratesetStatus))
        }
    }

    const scheduleDir = sort?.key === 'schedule' && sort.dir === 'DESC' ? -1 : 1
    const schedules = [...scheduleOrder].sort((a, b) => a.localeCompare(b) * scheduleDir)

    return schedules.map((schedule) => {
        const ratesetMap = bySchedule.get(schedule) ?? new Map<string, TariffRateAnalysisRow[]>()
        const dates = [...ratesetMap.keys()].sort((a, b) => b.localeCompare(a))
        const info = meta.get(schedule)
        return {
            schedule,
            tariffName: info?.name ?? null,
            tariffStatus: info?.status ?? null,
            ratesets: dates.map((date) => {
                const key = `${schedule}|${date}`
                return {
                    key,
                    schedule,
                    rateEffectiveDate: date,
                    ratesetStatus: ratesetStatus.get(key) ?? null,
                    rows: [...(ratesetMap.get(date) ?? [])].sort((a, b) =>
                        compareRateRows(a, b, sort, validationTableValue)
                    ),
                }
            }),
        }
    })
}
